import { get } from 'lodash-es';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyType } from '@ourbranch/lookups';

import { appConfig, isBranchEnv } from 'brand-config';
import Notification from 'core/helpers/notifications';

const badExteriorWallTypes = ['B', 'T', 'W'];

// Policy types eligible for the consolidated auto UW review (auto-only bundles). NOT 'FA'
// (Farm+Auto) — a bundle would need BOTH farm and auto review, which neither flow handles today;
// pending product confirmation on whether the bundle is sold. (#32744)
const autoUWReviewPolicyTypes = [policyType.Auto, policyType.AutoBundle, policyType.ARBundle];

// Single source of truth for "this offer must go through the auto UW review flow": the brand
// config defines auto UW questions AND the offer is an auto-based policy type. Brand-config-driven
// so any mutual can opt in — CFM is the only brand that populates appConfig.auto.uwQuestions today.
// Used by the needs-form check, the footer, the pre-bind screen, and the checkout guard so they
// can't drift.
export const offerUsesAutoUWReview = (selectedOption) =>
  !!appConfig.auto?.uwQuestions && autoUWReviewPolicyTypes.includes(selectedOption);

export const determineIfOfferNeedsUWForm = ({
  offer,
  priorQuoteWithPreBindUWRejections,
  setFormManually,
  selectedOption
}) => {
  if (setFormManually) {
    return true;
  }
  // if either home or auto verification is filled
  // need the != vs !== so we can identify if value is null or undefined
  if (
    offer?.quote?.preBindUWCheck?.homeVerification?.signedPropertyUWAttestation != null ||
    offer?.quote?.preBindUWCheck?.autoVerification?.signedPropertyUWAttestation != null
  ) {
    const { homeVerification, autoVerification } = offer?.quote?.preBindUWCheck ?? {};

    if (homeVerification?.signedPropertyUWAttestation && autoVerification?.signedPropertyUWAttestation) {
      // if form is signed for both, we don't need to show again
      return false;
    }
    if (
      (autoVerification?.ineligibleForAutoDueToUWSelfReport || priorQuoteWithPreBindUWRejections?.ineligibleForAuto) &&
      (homeVerification?.ineligibleForHomeDueToUWSelfReport || priorQuoteWithPreBindUWRejections?.ineligibleForHome)
    ) {
      // if ineligible for home and auto, return false
      return false;
    }

    // home
    if (selectedOption === 'H' || selectedOption === 'HB') {
      // if signed or ineligible
      if (
        homeVerification?.ineligibleForHomeDueToUWSelfReport ||
        priorQuoteWithPreBindUWRejections?.ineligibleForHome ||
        homeVerification?.signedPropertyUWAttestation
      ) {
        return false;
      }
      // eligible for home and hasn't signed
      return true;
    }

    // if we are here, then we are eligible
    if (selectedOption === 'HA') {
      if (offer.quote.home.homeLocation.state !== 'FL' && homeVerification?.signedPropertyUWAttestation) {
        // for now, all states (except for FL) only need home
        return false;
      }
      if (homeVerification?.signedPropertyUWAttestation && autoVerification?.signedPropertyUWAttestation) {
        return false;
      }
      return true;
    }
  } else if (
    (priorQuoteWithPreBindUWRejections?.ineligibleForHome && (selectedOption === 'H' || selectedOption === 'HB')) ||
    (priorQuoteWithPreBindUWRejections?.ineligibleForAuto && (selectedOption === 'A' || selectedOption === 'AB'))
  ) {
    return false;
  } else if (offer.quote.home && selectedOption?.includes('H')) {
    if (
      offer.quote.home.homeLocation.state !== 'FL' &&
      selectedOption?.includes('H') &&
      (offer?.quote?.home?.yearBuilt < 1940 ||
        (offer?.quote?.home.yearBuilt < 1971 && !badExteriorWallTypes.includes(offer?.quote?.home?.exteriorWallType)))
    ) {
      return true;
    }
    if (offer.quote.home.homeLocation.state === 'FL') {
      return true;
    }
    // all other cases
    return false;
  }
  // farm-based offers (F, Z, Y) need UW Form unless already attested
  else if (isFarmBasedPolicyType(selectedOption)) {
    const { farmVerification } = offer?.quote?.preBindUWCheck ?? {};
    return !farmVerification?.signedFarmUWAttestation;
  }
  // Auto UW review: agent must complete the pre-bind auto UW questionnaire (one attestation)
  // before checkout — mirrors the farm gate. Brand-config-gated: inert for any brand that doesn't
  // populate appConfig.auto.uwQuestions (only CFM does today). (#32744)
  else if (offerUsesAutoUWReview(selectedOption)) {
    const { autoVerification } = offer?.quote?.preBindUWCheck ?? {};
    return !autoVerification?.signedAutoUWAttestation;
  } else {
    return false;
  }
};

export const getUWNotification = ({ offer, priorQuoteWithPreBindUWRejections, needsPreBindUWForm }) => {
  const notifications = [];
  // if self reported issue
  if (
    Object.values(priorQuoteWithPreBindUWRejections).some((condition) => condition === true) ||
    get(offer, 'quote.preBindUWCheck.homeVerification.ineligibleForHomeDueToUWSelfReport', false) ||
    get(offer, 'quote.preBindUWCheck.homeVerification.ineligibleForHomeDueToUWSelfReport', false)
  ) {
    notifications.push(Notification.Offer.SelfReportedUWFlags);
  }

  // if needs form (farm offers have their own UW review flow). Mutual brands
  // (Forreston, Oakwood, CFM, etc.) don't use the Branch UW Review page, so
  // suppress the banner there. (#31071)
  const selectedOption = offer?.quote?.selectedOption;
  const isFarmOffer = isFarmBasedPolicyType(selectedOption) || selectedOption === 'FA';
  if (needsPreBindUWForm && !isFarmOffer && isBranchEnv) {
    notifications.push(Notification.Offer.OfferNeedsUWReview);
  }

  return notifications;
};

export const offerHasValidOptions = ({ offer, priorQuoteWithPreBindUWRejections, rejections }) => {
  if (offer?.options?.length < 1) {
    return false;
  }

  const ineligiblePolicyTypes = [];
  if (priorQuoteWithPreBindUWRejections?.ineligibleForAuto) {
    ineligiblePolicyTypes.push('A', 'AB', 'AR', 'HA');
  }

  if (priorQuoteWithPreBindUWRejections?.ineligibleForHome) {
    ineligiblePolicyTypes.push('H', 'HB', 'HA');
  }
  if (rejections?.length > 0) {
    rejections.forEach((rejection) => {
      if (rejection.type === 'auto') {
        ineligiblePolicyTypes.push('A', 'AB', 'AR', 'HA');
      }
      if (rejection.type === 'home') {
        ineligiblePolicyTypes.push('H', 'HB', 'HA');
      }
      if (rejection.type === 'renters') {
        ineligiblePolicyTypes.push('R', 'AR');
      }
      if (rejection.type === 'condo') {
        ineligiblePolicyTypes.push('C', 'CA');
      }
    });
  }

  const remainingOptions = offer.options.filter((o) => !ineligiblePolicyTypes.includes(o.type));

  return !!remainingOptions.length;
};
